import type { OnboardingPhase } from "@fastn/core";
/**
 * Every screen of the journey, with the URL it lives at under /onboarding and
 * the step of the first integration it belongs to. The URL is what a reload or
 * "Continue setup" lands on, so it is also what the journey stores.
 */
export const SCREENS = {
  welcome: "welcome",
  domain: "domain",
  domainChange: "domain/change",
  noProduct: "no-product",
  how: "how",
  research: "product",
  review: "product/review",
  about: "product/about",
  direction: "product/data",
  useCase: "product/use-case",
  build: "product/build",
  building: "product/building",
  integrations: "integrations",
  integrationBuilding: "integrations/building",
  embed: "embed",
  customers: "customers",
} as const;

export type ScreenId = keyof typeof SCREENS;

export const SCREEN_STEP: Record<ScreenId, 1 | 2 | 3 | 4 | null> = {
  welcome: null,
  domain: null,
  domainChange: null,
  noProduct: null,
  how: null,
  research: 1,
  review: 1,
  about: 1,
  direction: 1,
  useCase: 1,
  build: 1,
  building: 1,
  integrations: 2,
  integrationBuilding: 2,
  embed: 3,
  customers: 4,
};

export function onboardingPath(screen: ScreenId): string {
  return `/onboarding/${SCREENS[screen]}`;
}

/** The screen a stored path names, or null when it names none (an old journey, a typo). */
export function screenFromPath(path: string | null | undefined): ScreenId | null {
  if (!path) return null;
  const rest = path.replace(/^\/?onboarding\/?/, "").replace(/\/$/, "");
  const hit = (Object.keys(SCREENS) as ScreenId[]).find((id) => SCREENS[id] === rest);
  return hit ?? null;
}

/** Where step N starts when Home's "Continue setup" opens it. */
export const STEP_START: Record<1 | 2 | 3 | 4, ScreenId> = {
  1: "research",
  2: "integrations",
  3: "embed",
  4: "customers",
};

/**
 * Where a step starts for this journey: on its build when the agent has one
 * going for it, and step 1 on "What we found" (the design's 5) once research
 * has been read and saved, rather than on the research screen again.
 */
export function journeyStartOf(journey: {
  productThreadId: string | null;
  integrationThreadId: string | null;
  product?: { description: string | null } | null;
} | null | undefined) {
  return (step: 1 | 2 | 3 | 4): ScreenId =>
    step === 1 && journey?.productThreadId ? "building"
      : step === 1 && journey?.product?.description ? "review"
        : step === 2 && journey?.integrationThreadId ? "integrationBuilding"
          : STEP_START[step];
}

/**
 * Where "Continue setup" and /onboarding land: the screen the journey stored,
 * unless it belongs to a step after the first one not done, in which case that
 * step's start. A stored screen runs ahead of the steps when a screen moved on
 * before its step really finished (the build said done, registering the product
 * failed), and resuming there would show step 2 while the setup panel says 1.
 * `firstOpen` is the 0-based index of the first step not done, -1 when all are.
 */
export function resumeScreen(
  stored: ScreenId | null,
  firstOpen: number,
  startOf: (step: 1 | 2 | 3 | 4) => ScreenId = (step) => STEP_START[step],
): ScreenId | null {
  const open = firstOpen >= 0 ? ((firstOpen + 1) as 1 | 2 | 3 | 4) : null;
  if (!stored || !open) return stored;
  const step = SCREEN_STEP[stored];
  // A screen before the steps (welcome, the domain question) is behind them
  // once a step is done: the work moved on elsewhere, in an AI tool or on the
  // platform, so the journey resumes at the first step still open.
  if (!step) return firstOpen > 0 ? startOf(open) : stored;
  return step > open ? startOf(open) : stored;
}

const STEP_PHASE = ["product", "integrations", "embed", "customers"] as const;

/** Which part of the journey a screen belongs to, for analytics. */
export function screenPhase(screen: ScreenId): OnboardingPhase {
  const step = SCREEN_STEP[screen];
  if (step) return STEP_PHASE[step - 1]!;
  if (screen === "welcome" || screen === "how") return screen;
  return "domain";
}

export const PHASE_ORDER: readonly OnboardingPhase[] = ["welcome", "domain", "how", ...STEP_PHASE];

/** Navigation state that opens the setup panel on arrival in the platform. */
export const OPEN_SETUP_PANEL = "openSetupPanel";
